/* @flow */
import idx from 'idx';
import * as React from 'react';
import { Container } from 'flux/utils';
import Group from 'entities/Group';
import { graphql, compose } from 'react-apollo';
import { AnnouncementStream } from 'chat/components/AnnouncementStream';
import ANNOUNCEMENT_STREAM_VIEW_QUERY from '../queries/AnnouncementStreamViewQuery.gql';
import ANNOUNCEMENT_STREAM_VIEW_ORG_QUERY from '../queries/AnnouncementStreamViewOrgQuery.gql';
import ANNOUNCEMENT_STREAM_VIEW_STREAM_QUERY from '../queries/AnnouncementStreamViewStreamQuery.gql';
import AnnouncementComposerButton from 'groups/components/AnnouncementComposerButton';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import ConversationHeader from 'chat/components/ConversationHeader';
import CurrentUser from 'auth/CurrentUser';
import { formatRole } from 'utils/roleHelpers';
import UnreadActions from 'actions/UnreadActions';
import UnreadStore from 'stores/UnreadStore';
import { TARGET_TYPES } from 'entities/Message';

import type { GroupTargetRole } from 'entities/Message';
import type { AnnouncementStreamView as AnnouncementStreamViewQueryType } from '../queries/__generated__/AnnouncementStreamView';
import type { AnnouncementStreamViewOrg_organizations } from '../queries/__generated__/AnnouncementStreamViewOrg';

import 'chat/components/styles/AnnouncementsConversationView.scss';

type Props = {
  params: *,
  group: Group,
  data: ReactApolloData<AnnouncementStreamViewQueryType>,
  organization: AnnouncementStreamViewOrg_organizations,
  streamName: string,
};

type State = {
  unreadCount: number,
};

class AnnouncementStreamView extends React.Component<Props, State> {
  static getStores() {
    return [UnreadStore];
  }

  static calculateState(prevState: ?State, props: Props): State {
    return {
      unreadCount: UnreadStore.getUnreadCount(props.params.uuid) || 0,
    };
  }

  mapStreamNameToRole(streamName: string) {
    // TODO: this is a very hacky way of supporting RBM
    // we should remove this when query-based targeting happens
    const roles = {
      Students: 'student',
      Parents: 'parent',
      Teachers: 'teacher',
      Admins: 'admin',
    };
    return roles[streamName] || 'everyone';
  }

  clearUnreads = () => {
    if (this.state.unreadCount === 0) {
      return;
    }
    UnreadActions.clearThreadUnreads(this.props.params.uuid);
  };

  shouldLimitSendFunctionality = (group: *) => {
    if (!group) {
      return false;
    }
    const currentUser = CurrentUser.getUser();
    const hasFreeProductLimitFeature =
      currentUser && currentUser.hasFeature('free_product_limits.web.apr-19');
    return hasFreeProductLimitFeature && group.hasLimitedSendFunctionality;
  };

  renderComposer(type: 'organization' | 'group', id: number, name: string, role: GroupTargetRole) {
    const { uuid } = this.props.params;
    const groupRoleLabel = formatRole(role) || 'Everyone';
    const selectedFilters =
      role === 'everyone' ? [] : [{ type: 'roles', value: { id: role, label: groupRoleLabel } }];
    const groupTarget = {
      name,
      type: TARGET_TYPES.GROUP_TARGET,
      group_id: id,
      selectedFilters,
      role,
    };
    const districtTarget = {
      name,
      org_id: id,
      type: TARGET_TYPES.ORGANIZATION_TARGET,
      selectedFilters: [],
    };
    const target = type === 'organization' ? districtTarget : groupTarget;

    return (
      <AnnouncementComposerButton
        className="AnnouncementsConversationView-body-composer"
        onMessageSent={() => {}}
        target={target}
        threadUuid={uuid}
      />
    );
  }

  render() {
    const { uuid } = this.props.params;
    const { data } = this.props;

    if (data && data.loading) {
      return <CenteredLoadingIndicator />;
    }

    const group = idx(data, _ => _.classes[0]);

    if (!group) {
      return null;
    }

    const { streamName, organization } = this.props;
    const role = streamName ? this.mapStreamNameToRole(streamName) : 'everyone';
    const title =
      streamName || streamName === group.className || streamName === 'Everyone'
        ? group.className
        : `${group.className} > ${streamName}`;

    // if it's a district,
    // compose message to the organization
    // otherwise compose message to the class.
    // TODO: it'd be better to get the target from the stream itself
    const composer =
      organization && group.type === 'district'
        ? this.renderComposer('organization', organization.id, organization.name, role)
        : this.renderComposer('group', group.id, group.className, role);

    return (
      <div className="AnnouncementsConversationView">
        <ConversationHeader ref="header" title={title} subtitle="Announcement history" />
        <div className="AnnouncementsConversationView-body">
          <div className="AnnouncementsConversationView-body-wrap">
            <AnnouncementStream
              key={uuid}
              uuid={uuid}
              onScrollToBottom={this.clearUnreads}
              bottomLineComposer={composer}
              shouldLimitSendFunctionality={this.shouldLimitSendFunctionality(group)}
              groupCode={group.name}
            />
          </div>
        </div>
      </div>
    );
  }
}

const withClass = graphql(ANNOUNCEMENT_STREAM_VIEW_QUERY, {
  options: ({ group }) => ({ variables: { uuid: group.get('uuid') } }),
});

const withOrg = graphql(ANNOUNCEMENT_STREAM_VIEW_ORG_QUERY, {
  options: ({ group }) => ({ variables: { id: group.getStaffClassOrgId() } }),
  skip: ({ group }) => !group.isStaffClass(),
  props: ({ data }) => ({ organization: idx(data, _ => _.organizations[0]) }),
});

const withStream = graphql(ANNOUNCEMENT_STREAM_VIEW_STREAM_QUERY, {
  options: ({ params }) => ({ variables: { uuid: params.uuid } }),
  props: ({ data }) => ({ streamName: idx(data, _ => _.entityStreams[0].name) }),
});

export { AnnouncementStreamView };
export default compose(
  withClass,
  withStream,
  withOrg
)(
  Container.create(AnnouncementStreamView, {
    withProps: true,
    pure: false,
  })
);
